import { pickBy } from "lodash-es";

import store from "@/app/store";
import { selectCurrentBuilding } from "@/features/cartography/cartographySlice.selectors";
import {
  type GenericOverlayFeature,
  type IconOverlayFeature,
  type OverlayFeatureCollection,
  OverlayFeatureTypes,
} from "@/features/editor/types";

/////////////////////////////////////////////////////////////////////////////
// Util callbacks
/////////////////////////////////////////////////////////////////////////////

/**
 * Generates a duplicate of a given name by appending a random suffix.
 * The suffix is a 5-character alphanumeric string (base36),
 * which should be unique enough for most practical purposes.
 * If the name already appears to be a duplicate (ends with "_copy_<suffix>"),
 * the function strips off that suffix first.
 *
 * @param name - The original name (or a duplicate name) to copy.
 * @returns A new name string, e.g. "Building_copy_ab12c"
 */
export function duplicateName(name: string): string {
  // Regular expression to match a name ending with "_copy_" followed by 5 alphanumeric characters.
  const regex = /^(.*)_copy_[a-z0-9]{5}$/;
  // If the name matches the duplicate pattern, use the captured base name.
  const baseName = regex.test(name) ? name.replace(regex, "$1") : name;
  // Generate a random 5-character alphanumeric suffix.
  const randomSuffix = Math.random().toString(36).slice(2, 7);
  return `${baseName}_copy_${randomSuffix}`;
}

export const getCartographyFeatureIcon = (d: IconOverlayFeature) => {
  const url = d.properties.url || "/images/minimal-dot.png";
  const [width, height] = d.properties.iconDimensions ?? [200, 200];
  return {
    anchorX: width / 2,
    anchorY: height / 2,
    height,
    url,
    width,
  };
};

export const generateRandomCartographyFeatureUid = (length = 24): string => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from(
    { length },
    () => chars[Math.floor(Math.random() * chars.length)],
  ).join("");
};

export const readOverlayFeaturesFromCustomField = (customFieldData: string) => {
  const geojson = JSON.parse(
    unscapeJsonStringChars(customFieldData),
  ) as OverlayFeatureCollection;
  const currentBuilding = selectCurrentBuilding(store.getState());
  return geojson.features.reduce(
    (acc, f) => {
      const instance: GenericOverlayFeature = {
        ...f,
        id: generateRandomCartographyFeatureUid(),
        properties: {
          ...f.properties,
          // Map elements with floor level to floor ID
          floorId:
            f.properties.floorLevel != null
              ? currentBuilding?.getFloorByLevel(f.properties.floorLevel)?.id
              : f.properties.floorId,
          // TODO when 3d models were first introduced, type field did not exist.
          // We keep this fallback to prevent old buildings from losing all their 3d models
          type: f.properties.type ?? OverlayFeatureTypes.model,
        },
      } as GenericOverlayFeature;
      acc[instance.id] = instance;
      return acc;
    },
    {} as Record<string, GenericOverlayFeature>,
  );
};

const unscapeJsonStringChars = (jsonString: string) =>
  jsonString.replace(/\\\\n/g, "\\n").replace(/\\n/g, "\\n");

const escapeJsonStringChars = (jsonString: string) =>
  jsonString
    .replace(/\\'/g, "\\'")
    .replace(/\\&/g, "\\&")
    .replace(/\\"/g, '\\"')
    // TODO: review as it breaks safari with iOS < 16.3
    //.replace(/(?<!\\)\\n/g, "\\\\n")
    .replace(/\\r/g, "\\\\r")
    .replace(/\\t/g, "\\\\t")
    .replace(/\\b/g, "\\\\b")
    .replace(/\\f/g, "\\\\f");

const generateGeojsonFromFeatures = (
  features: Record<string, GenericOverlayFeature>,
  keepPois = false,
): OverlayFeatureCollection | null => {
  const currentBuilding = selectCurrentBuilding(store.getState());
  return {
    features: Object.values(features)
      .filter((f) => !keepPois && f.properties.type !== OverlayFeatureTypes.poi)
      .map((f) => {
        // Leave out the ID, and replace floorId with floorLevel
        const { id: _unused, ...rest } = f;
        const modifiedFeature = {
          ...rest,
          properties: {
            ...f.properties,
            floorId: undefined,
            floorLevel: currentBuilding.getFloorById(f.properties.floorId)
              ?.level,
          },
        } as GenericOverlayFeature & {
          properties: { floorLevel: number };
        };
        return modifiedFeature;
      }) as GenericOverlayFeature[],
    type: "FeatureCollection",
  };
};

export const exportCartographyFeaturesToGeojsonString = (
  features: Record<string, GenericOverlayFeature>,
  keepPois = false,
) => {
  const geojsonObj = generateGeojsonFromFeatures(features, keepPois);
  return escapeJsonStringChars(JSON.stringify(geojsonObj));
};

export const filterOverlayFeatures = (
  features: Record<string, GenericOverlayFeature>,
  featureTypes: OverlayFeatureTypes[],
  filterText?: string,
): Record<string, GenericOverlayFeature> => {
  const lowerFilterText = filterText?.toLowerCase()?.trim();
  const hasFeatureTypes = featureTypes.length > 0;

  return pickBy(features, (value: GenericOverlayFeature) => {
    if (hasFeatureTypes && !featureTypes.includes(value.properties.type)) {
      return false;
    }
    if (
      lowerFilterText &&
      (!value?.properties?.name ||
        !value.properties.name.toLowerCase().includes(lowerFilterText))
    ) {
      return false;
    }
    return true;
  });
};
